import { useState, useEffect, useCallback } from 'react'
import { useGameStore } from '@store/gameStore'
import { GAME_CONFIG } from '@config/gameConfig'
import { AdModal } from '@components/AdModal'
import './LoadingScreen.css'



export function LoadingScreen() {
    const [progress, setProgress] = useState(0)
    const [timeRemaining, setTimeRemaining] = useState<number>(GAME_CONFIG.loading.baseTime)
    const [isShowingAd, setIsShowingAd] = useState(false)

    const setScreen = useGameStore(state => state.setScreen)
    const setPhase = useGameStore(state => state.setPhase)
    const trackMoneySpent = useGameStore(state => state.trackMoneySpent)
    const trackAdWatched = useGameStore(state => state.trackAdWatched)
    const trackWaitTime = useGameStore(state => state.trackWaitTime)
    const addGems = useGameStore(state => state.addGems)



    // Progress to next screen when complete
    const completeLoading = useCallback(() => {
        setPhase('tutorial')
        setScreen('tutorial')
    }, [setPhase, setScreen])

    // Natural progress timer
    useEffect(() => {
        if (progress >= 100) {
            completeLoading()
            return
        }

        if (isShowingAd) return

        const startTime = Date.now()
        const interval = setInterval(() => {
            const elapsed = (Date.now() - startTime) / 1000
            const progressPerSecond = 100 / GAME_CONFIG.loading.baseTime
            const newProgress = Math.min(100, progress + (elapsed * progressPerSecond))

            setProgress(newProgress)
            setTimeRemaining(Math.max(0, Math.ceil((100 - newProgress) / progressPerSecond)))

            if (newProgress >= 100) {
                trackWaitTime(GAME_CONFIG.loading.baseTime * 1000)
                clearInterval(interval)
            }
        }, 100)

        return () => clearInterval(interval)
    }, [progress, isShowingAd, trackWaitTime, completeLoading])

    // Watch Ad Handler
    const handleWatchAd = () => {
        setIsShowingAd(true)
    }

    const handleAdComplete = () => {
        // Instant finish logic
        setTimeRemaining(0)
        setProgress(100)
        trackAdWatched()
        setIsShowingAd(false)
    }

    // Instant Start Handler (Pay)
    const handleInstantStart = () => {
        trackMoneySpent(GAME_CONFIG.loading.instantCost)
        addGems(50) // Bonus gems for paying
        setProgress(100)
        setTimeRemaining(0)
    }

    // Format time as MM:SS
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    return (
        <div className="loading-screen">
            {/* Main Content */}
            <div className="loading-screen__content">
                <div className="loading-screen__logo">
                    <img src="./logo.png" alt="Pay2Lose" className="loading-screen__logo-img" />
                </div>

                <div className="loading-screen__progress-container">
                    <div className="loading-screen__progress-label">
                        <span>Lädt...</span>
                        <span>{Math.floor(progress)}%</span>
                    </div>
                    <div className="progress">
                        <div
                            className="progress__bar"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    {timeRemaining > 0 && (
                        <div className="loading-screen__timer-center">
                            <span className="loading-screen__timer-icon">⏳</span>
                            <span className="loading-screen__timer-value">Noch {formatTime(timeRemaining)}</span>
                        </div>
                    )}
                </div>

                <div className="loading-screen__options">
                    <button
                        className="loading-screen__btn loading-screen__btn--ad"
                        onClick={handleWatchAd}
                        disabled={isShowingAd || progress >= 100}
                    >
                        <span className="loading-screen__btn-icon">📺</span>
                        <span className="loading-screen__btn-main">Werbung schauen</span>
                        <span className="loading-screen__btn-sub">Sofort fertig!</span>
                    </button>

                    <button
                        className="loading-screen__btn loading-screen__btn--gold"
                        onClick={handleInstantStart}
                        disabled={progress >= 100}
                    >
                        <span className="loading-screen__btn-icon">💸</span>
                        <span className="loading-screen__btn-main">Sofort starten</span>
                        <span className="loading-screen__btn-sub">{GAME_CONFIG.loading.instantCost.toFixed(2)}€</span>
                    </button>
                </div>

                <p className="loading-screen__hint">
                    💡 Tipp: Premium-Spieler laden 10x schneller!
                </p>
            </div>

            {/* Ad Modal */}
            {isShowingAd && (
                <AdModal
                    onComplete={handleAdComplete}
                    onCancel={() => setIsShowingAd(false)}
                />
            )}
        </div>
    )
}
